import React, { createContext, memo, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import moment from 'moment';
import { useIntl } from 'react-intl';

import { noop } from 'ui-components/default-props/noop';

import { EnhancedReturnStatus } from '../../../enums/enhanced-return-status';
import { useCanCreateEnhancedReturn } from '../../../store/returns/returns.helpers';
import { useReturnNavigation } from '../hooks/use-return-navigation';
import { getReturnsForSalesOrder } from '../../../store/returns/returns.api';

import { messages } from './messages';

const initialValue = {
  loadEnhancedReturns: noop,
  registerPendingReturn: noop,
  loading: false,
  returns: [],
  pendingReturn: null,
};

const createDateTimeComparison = (a, b) => {
  const { createDateTime: createDateTimeA } = a;
  const { createDateTime: createDateTimeB } = b;
  if (!createDateTimeA || !createDateTimeB) {
    return 0;
  }

  return moment(createDateTimeA).diff(createDateTimeB);
};

/**
 * @type {React.Context<{
 *   loadEnhancedReturns: () => void;
 *   registerPendingReturn: (requestId: string) => void;
 *   loading: boolean;
 *   returns: Array<object>;
 *   pendingReturn: object | null;
 * }>}
 */
export const EnhancedReturnsContext = createContext(initialValue);

export const useEnhancedReturnsContext = () => useContext(EnhancedReturnsContext);

/**
 * A context provider that fetches and provides enhanced returns data for the current sales order.
 * When the sales order number changes, we will load the returns data for that order.
 *
 * And since the order record in SS isn't updated when a new enhanced return is created, we will
 * support the option to reload the returns data from SSR when needed (e.g. when a new enhanced return is created).
 * @param {object} props - Component props
 * @param {React.ReactNode} props.children - Child components
 * @param {string} [props.orderNumber] - The order number
 * @param {string} [props.salesOrderId] - The sales order ID
 * @param {string} [props.storeGuid] - The store GUID
 * @returns {React.ReactElement} The context provider component
 */
export const EnhancedReturnsContextProvider = memo(({ children, orderNumber, salesOrderId, storeGuid }) => {
  const [returns, setReturns] = useState([]);
  const [loading, setLoading] = useState(false);
  const loadingRef = useRef(false);
  const [pendingReturn, setPendingReturn] = useState(null);

  const shouldUseEnhancedReturn = useCanCreateEnhancedReturn(salesOrderId);
  const { createPendingEnhancedReturnPlaceholderId } = useReturnNavigation();
  const intl = useIntl();

  const loadEnhancedReturns = useCallback(() => {
    // When flag is off, preserve old behavior: only load if store supports enhanced returns
    if (loadingRef.current || !shouldUseEnhancedReturn || !orderNumber || !storeGuid) {
      return;
    }

    loadingRef.current = true;
    setLoading(true);

    getReturnsForSalesOrder(orderNumber, storeGuid)
      .then((response) => {
        const responseReturns = (response?.results ?? []).sort(createDateTimeComparison);
        setReturns(responseReturns);
      })
      .catch((err) => {
        // oxlint-disable-next-line no-console
        console.error('loadEnhancedReturns - err', err);
      })
      .finally(() => {
        loadingRef.current = false;
        setLoading(false);
      });
    // Deliberately omitting returns from the dependency array to avoid infinite loop
  }, [shouldUseEnhancedReturn, orderNumber, storeGuid]);

  // When the sales order id changes, we will load the enhanced returns data for that order
  useEffect(() => {
    loadEnhancedReturns();
  }, [loadEnhancedReturns, salesOrderId]);

  const registerPendingReturn = useCallback(
    (requestId) => {
      setPendingReturn({
        id: createPendingEnhancedReturnPlaceholderId(requestId),
        name: intl.formatMessage(messages.inProgressReturnLabel),
        createDateTime: new Date().toISOString(),
        status: EnhancedReturnStatus.PendingCompletion.key,
      });
    },
    [createPendingEnhancedReturnPlaceholderId, intl],
  );

  const value = useMemo(
    () => ({
      loadEnhancedReturns,
      loading,
      returns,
      pendingReturn,
      registerPendingReturn,
    }),
    [loadEnhancedReturns, loading, returns, pendingReturn, registerPendingReturn],
  );

  return <EnhancedReturnsContext.Provider value={value}>{children}</EnhancedReturnsContext.Provider>;
});
